import { ReactText, useState } from 'react'
import { capitalize } from 'lodash'
import { useHover } from '@react-aria/interactions'
import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { useHistory } from 'react-router-dom'

import { Button } from 'components/Button'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { Popover } from 'components/Popover/Popover'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import {
  FileAccessGrantAccessor,
  AssetAccessGrantWithParent,
  Permission,
  RecipientsSelectorValueType,
  Workspace,
} from 'types'
import { ShareSheet } from 'components/ShareSheet/ShareSheet'
import { ReactComponent as PlusIcon } from 'assets/icons/plusSmall16.svg'
import { useExpandGroupRow } from './useExpandGroupRow'
import { ReactComponent as RightChevron } from 'assets/icons/chevronRight12.svg'
import { ReactComponent as DownChevron } from 'assets/icons/chevronDown12.svg'
import { ChannelValue, UserValue } from 'components/ShareSheet/RecipientSelector/Types'
import { useModals } from 'almanac-ui/Modal'
import { FolderClosed } from 'almanac-ui/icons'
import { ReactComponent as AlertIcon } from 'assets/icons/alert15.svg'
import { ReactComponent as CantMergeIcon } from './CantMerge.svg'
import { AccessDropdown } from './AccessDropdown'
import { AccessorAvatar } from './AccessorAvatar'
import { AccessRowProps, DropdownOptions } from '../Types'
import Styles from '../ShareSheet.module.scss'

const ExpandedRow = ({ row }: { row: FileAccessGrantAccessor }) => {
  const { childRows, expanded, toggleExpanded } = useExpandGroupRow({ type: row.type, groupId: row.id })

  return (
    <>
      <div
        key={`row-${row.id}`}
        className={cx(Styles.expandedRow, { [Styles.withHover]: !!row.member_count })}
        onClick={toggleExpanded}
      >
        <div className={Styles.expandedRowContents}>
          <AccessorAvatar type={row.type} avatarUrl={row.avatar_url} name={row.name} />
          <span className={Styles.name}>{row.name}</span>
          {!!row.member_count && row.member_count > 0 && (
            <div className={Styles.groupItems}>
              <div className={Styles.counter}>{row.member_count}</div>
              {expanded ? <DownChevron /> : <RightChevron />}
            </div>
          )}
        </div>
      </div>
      <div className={cx(Styles.expandedContents, { [Styles.showing]: expanded })}>
        {childRows.map((row, index) => (
          <ExpandedRow key={`expanded-${row.id}-${index}`} row={row} />
        ))}
      </div>
    </>
  )
}

const StaticAccess = observer(
  ({
    accessLevel,
    rootAsset,
    isLayer,
    isDefaultFolder,
    workspace,
  }: {
    accessLevel: Permission
    rootAsset: AssetAccessGrantWithParent | undefined
    isLayer: boolean
    isDefaultFolder: boolean
    workspace: Workspace | undefined
  }) => {
    const history = useHistory()
    const capitalizedAccess = capitalize(accessLevel)
    const { hoverProps, isHovered } = useHover({})
    const { openModal, closeAllModals } = useModals()

    if (accessLevel === 'owner' && !isDefaultFolder && workspace && !workspace.guest) {
      return (
        <Tooltip label="To transfer ownership, invite a member and go to their access settings below" hide={isLayer}>
          <div className={Styles.inheritedAccess}>{capitalizedAccess}</div>
        </Tooltip>
      )
    }

    if (rootAsset) {
      const { has_root_access: hasRootAccess } = rootAsset
      return (
        <div className={Styles.popoverContainer} {...hoverProps}>
          {isHovered && (
            <Popover className={Styles.inheritancePopover} noOverlay isDismissable onClose={() => {}}>
              <Button
                variant="tertiary"
                size="sm"
                frontIcon={FolderClosed}
                onPress={() => {
                  if (hasRootAccess) {
                    openModal(ShareSheet, {
                      file: {
                        id: rootAsset.id,
                        type: 'folder',
                      },
                    })
                    return
                  }

                  if (rootAsset.url) {
                    history.push(rootAsset.url)
                  }
                  setTimeout(() => {
                    closeAllModals()
                  }, 100)
                }}
              >
                {rootAsset.title}: {capitalizedAccess}
              </Button>
            </Popover>
          )}
          <div className={Styles.inheritedAccess}>{capitalizedAccess}</div>
        </div>
      )
    }

    return <div className={Styles.inheritedAccess}>{capitalizedAccess}</div>
  },
)

export const AccessRow = observer(
  ({
    access,
    invited,
    userCanEdit,
    asset,
    onRemoveAccess,
    onTransfer,
    onTakeOwnership,
    onInvite,
    onUpdateAccess,
    superAdminMode,
    childMembers,
  }: AccessRowProps) => {
    const {
      id,
      cannot_merge_layer,
      root_asset,
      accessor: { id: accessorId, is_current_user, name, type, email, avatar_url, member_count, guest },
      access: accessLevel,
    } = access
    const capitalizedAccess = capitalize(accessLevel)
    const { hoverProps, isHovered } = useHover({})
    const { openModal } = useModals()
    const { isMobile } = useResponsiveDesign()
    const [showInheritedInfo, setShowInheritedInfo] = useState(false)
    const workspace = useCurrentWorkspace()
    const modifiedAccess = accessLevel === 'owner' && !invited ? Permission.Edit : accessLevel
    const showTakeOwnership =
      workspace &&
      !workspace.guest &&
      ((!invited && is_current_user) || (accessLevel === 'owner' && !is_current_user && userCanEdit)) &&
      !asset?.default_folder &&
      asset?.type !== 'layer'
    const showTransfer =
      type === RecipientsSelectorValueType.User && !guest && !asset?.default_folder && workspace && !workspace.guest
    const { childRows, expanded, toggleExpanded } = useExpandGroupRow({
      type,
      groupId: type === 'Channel' ? accessorId : undefined,
      childMembers,
    })

    const handleOptionClick = (option: ReactText) => {
      switch (option) {
        case 'remove':
          return onRemoveAccess({ access, hasParentFolder: asset?.parent?.type === 'folder' })
        case 'transfer':
          return onTransfer?.({ access })
        case 'takeOwnership':
          return onTakeOwnership?.()
        default:
          if (id) onUpdateAccess?.(id, option as Permission)
      }
    }

    const buildRecipient = (accessor: FileAccessGrantAccessor) => {
      if (accessor.type === 'User') {
        return {
          type: RecipientsSelectorValueType.User,
          value: { id: accessor.id, display_name: accessor.name },
        } as UserValue
      }

      return {
        type: RecipientsSelectorValueType.Channel,
        value: { name: accessor.name, id: accessor.id, members_count: accessor.member_count },
      } as ChannelValue
    }

    const inviteEntity = (accessor: FileAccessGrantAccessor) => {
      const recipient = buildRecipient(accessor)

      onInvite?.(recipient)
    }

    const buildOptions = () => {
      if (is_current_user || accessLevel === 'owner') return ['takeOwnership'] as DropdownOptions[]
      if (asset?.type === 'folder') {
        if (showTransfer) {
          return [Permission.Edit, Permission.Read, 'transfer', 'remove'] as DropdownOptions[]
        }
        return [Permission.Edit, Permission.Read, 'remove'] as DropdownOptions[]
      }
      if (asset?.type === 'layer') return [Permission.Edit, 'remove'] as DropdownOptions[]
      if (showTransfer) {
        if (asset?.type === 'design')
          return [Permission.Edit, Permission.Read, 'transfer', 'remove'] as DropdownOptions[]
        return [Permission.Edit, Permission.Comment, Permission.Read, 'transfer', 'remove'] as DropdownOptions[]
      }

      if (asset?.type === 'design') return [Permission.Edit, Permission.Read, 'remove'] as DropdownOptions[]
      return [Permission.Edit, Permission.Comment, Permission.Read, 'remove'] as DropdownOptions[]
    }

    return (
      <>
        <div
          className={cx(Styles.accessRow, {
            [Styles.withClickAction]: [
              RecipientsSelectorValueType.Channel,
              RecipientsSelectorValueType.LayerEditors,
            ].includes(type),
          })}
          onClick={toggleExpanded}
        >
          <div className={Styles.accessRowLeft}>
            <AccessorAvatar type={type} avatarUrl={avatar_url} name={name} />
            <div className={Styles.contents}>
              <span
                className={cx(Styles.name, { [Styles.longName]: type === RecipientsSelectorValueType.LayerEditors })}
              >
                {type === RecipientsSelectorValueType.Company && 'Everyone at '}
                {name}
              </span>
              {guest && <span className={Styles.guestText}>Guest</span>}
              {cannot_merge_layer && !isMobile && (
                <Tooltip label="Can’t merge layers (need Edit access on main doc)">
                  <div className={Styles.icon}>
                    <CantMergeIcon />
                  </div>
                </Tooltip>
              )}
              {!!member_count && (
                <div className={Styles.groupItems}>
                  <div className={Styles.counter}>{member_count}</div>
                  {expanded ? <DownChevron /> : <RightChevron />}
                </div>
              )}
              {type === 'User' && name !== email && <div className={Styles.hoverEmail}>{email}</div>}
            </div>
          </div>
          <div className={Styles.accessRowRight}>
            {!isMobile && !invited && ['User', 'Channel'].includes(access.accessor.type) && (
              <Button
                className={Styles.inviteButton}
                frontIcon={PlusIcon}
                variant="tertiary"
                size="md"
                onPress={() => inviteEntity(access.accessor)}
              >
                Invite
              </Button>
            )}
            {!showTakeOwnership &&
            ((!superAdminMode && !userCanEdit) ||
              !invited ||
              accessLevel === 'owner' ||
              (is_current_user && (asset?.default_folder || asset?.type === 'layer' || !!workspace?.guest))) ? (
              <StaticAccess
                rootAsset={root_asset}
                accessLevel={modifiedAccess}
                isLayer={asset?.type === 'layer'}
                isDefaultFolder={!!asset?.default_folder}
                workspace={workspace}
              />
            ) : (
              <div className={Styles.rightContents}>
                {access.higher_inherited_access && (
                  <Tooltip
                    label={`Inherited ${capitalize(access.higher_inherited_access)} Access`}
                    tooltipState={isMobile ? { isOpen: showInheritedInfo } : {}}
                  >
                    <div className={Styles.icon} onClick={() => setShowInheritedInfo(!showInheritedInfo)}>
                      <AlertIcon />
                    </div>
                  </Tooltip>
                )}

                <div className={Styles.popoverContainer} {...hoverProps}>
                  {!!root_asset && isHovered && (
                    <Popover className={Styles.inheritancePopover} noOverlay isDismissable onClose={() => {}}>
                      <Button
                        variant="tertiary"
                        size="sm"
                        className={Styles.inheritanceButton}
                        frontIcon={FolderClosed}
                        onPress={() => {
                          openModal(ShareSheet, {
                            file: {
                              id: root_asset.id,
                              type: 'folder',
                            },
                          })
                        }}
                      >
                        {root_asset.title}: {capitalizedAccess}
                      </Button>
                    </Popover>
                  )}
                  <AccessDropdown
                    options={buildOptions()}
                    handleClick={handleOptionClick}
                    currentPermission={modifiedAccess}
                    size="sm"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={cx(Styles.expandedContents, { [Styles.showing]: expanded })}>
          {childRows.map((row, index) => (
            <ExpandedRow key={`expanded-${row.id}-${index}`} row={row} />
          ))}
        </div>
      </>
    )
  },
)
